import type { CalculationResult } from "../engine";
import { formatMxn } from "../engine/money";
import { RealEstateExplanation } from "./RealEstateExplanation";

const categoryLabels: Record<string, string> = {
  earning: "Percepciones",
  benefit: "Prestaciones",
  indemnity: "Indemnizaciones",
  litigation: "Escenario litigioso",
  tax: "Impuestos",
  deduction: "Deducciones",
  employer: "Costo patronal",
  transaction: "Operación y exención",
  basis: "Costo y ganancia gravable",
  federal: "ISR federal",
  state: "Pago estatal acreditable",
};

export function ResultPanel({
  result,
  onSave,
  saving,
}: {
  result: CalculationResult;
  onSave?: () => void;
  saving?: boolean;
}) {
  const isRealEstate = result.kind === "real-estate-isr";
  const realEstate = result.realEstate;
  const groups = result.lines.reduce<
    Record<string, CalculationResult["lines"]>
  >((accumulator, line) => {
    (accumulator[line.category] ??= []).push(line);
    return accumulator;
  }, {});
  return (
    <section className="result-card" aria-live="polite" id="resultado">
      <header className="result-card__header">
        <div>
          <span className="eyebrow">Resultado estimado</span>
          <h2>{result.title}</h2>
          <p className="result-card__version">
            Matriz normativa {result.rulesVersion}
          </p>
        </div>
        <div className="result-card__actions no-print">
          {onSave && (
            <button
              className="button button--outline"
              onClick={onSave}
              disabled={saving}
            >
              {saving ? "Guardando…" : "Guardar cálculo"}
            </button>
          )}
          <button className="button button--dark" onClick={() => window.print()}>
            Imprimir / guardar PDF
          </button>
        </div>
      </header>

      <div className="result-total">
        <span>{isRealEstate ? "Pago provisional estimado de ISR" : "Total neto estimado"}</span>
        <strong>{formatMxn(isRealEstate ? result.totals.isr : result.totals.net)}</strong>
        {isRealEstate && realEstate ? (
          <small>
            Entidad federativa {formatMxn(realEstate.statePayment)} · Federación{" "}
            {formatMxn(realEstate.federalBalance)}
          </small>
        ) : (
          <small>
            Bruto {formatMxn(result.totals.gross)} · ISR{" "}
            {formatMxn(result.totals.isr)} · IMSS{" "}
            {formatMxn(result.totals.socialSecurity)}
          </small>
        )}
      </div>

      <dl className="result-metrics">
        {isRealEstate && realEstate ? (
          <>
            <div>
              <dt>Ingreso exento</dt>
              <dd>{formatMxn(realEstate.exemptConsideration)}</dd>
            </div>
            <div>
              <dt>Ingreso no exento</dt>
              <dd>{formatMxn(realEstate.taxableConsideration)}</dd>
            </div>
            <div>
              <dt>Ganancia gravable</dt>
              <dd>{formatMxn(realEstate.taxableGain)}</dd>
            </div>
            <div>
              <dt>Ganancia por año ({realEstate.elapsedYears})</dt>
              <dd>{formatMxn(realEstate.gainPerYear)}</dd>
            </div>
          </>
        ) : (
          <>
            <div>
              <dt>Salario diario</dt>
              <dd>{formatMxn(result.salary.daily)}</dd>
            </div>
            <div>
              <dt>Salario diario integrado</dt>
              <dd>{formatMxn(result.salary.integratedDaily)}</dd>
            </div>
            <div>
              <dt>Parte exenta</dt>
              <dd>{formatMxn(result.totals.exempt)}</dd>
            </div>
            <div>
              <dt>Parte gravada</dt>
              <dd>{formatMxn(result.totals.taxable)}</dd>
            </div>
          </>
        )}
      </dl>

      {isRealEstate && realEstate ? (
        <RealEstateExplanation details={realEstate} />
      ) : (
        <div className="result-groups">
          {Object.entries(groups).map(([category, lines]) => (
            <section key={category} className="result-group">
              <h3>{categoryLabels[category] || category}</h3>
              <div className="result-lines">
                {lines.map((line) => (
                  <div className="result-line" key={line.key}>
                    <div>
                      <span>{line.label}</span>
                      {line.legalBasis && <small>{line.legalBasis}</small>}
                      {line.note && <small>{line.note}</small>}
                    </div>
                    <strong className={line.amount < 0 ? "negative" : ""}>
                      {formatMxn(line.amount)}
                    </strong>
                  </div>
                ))}
              </div>
            </section>
          ))}
        </div>
      )}

      <div className="result-notes">
        <section>
          <h3>Supuestos usados</h3>
          <ul>
            {result.assumptions.map((item) => (
              <li key={item}>{item}</li>
            ))}
          </ul>
        </section>
        <section className="result-warning">
          <h3>Antes de tomar una decisión</h3>
          <ul>
            {result.warnings.map((item) => (
              <li key={item}>{item}</li>
            ))}
          </ul>
        </section>
      </div>
      <p className="print-disclaimer">
        {isRealEstate
          ? "Resultado informativo generado por LegalFlow. No sustituye la determinación del fedatario, la escritura, los comprobantes fiscales ni la declaración anual."
          : "Resultado informativo generado por LegalFlow. No sustituye un recibo CFDI, una determinación del SUA, un convenio ratificado ni asesoría sobre un expediente concreto."}
      </p>
    </section>
  );
}
